import { useRef, useState } from "react";
import {
  CheckboxCustomEvent,
  IonCheckbox,
  IonContent,
  IonItem,
  IonList,
  IonModal,
} from "@ionic/react";
import { SubjectModel } from "model";
import { Colors } from "swing-components";

import { translate } from "~utils";

import { ModalHeader } from "../ModalHeader";
import { SelectItem } from "../SelectItem";
import { useSubjects } from "./hooks.ts";
import { translateSelectedSubjects } from "./utils.ts";

type SubjectFieldProps = {
  testData?: SubjectModel[]; // optional field for testing and storybook use only
  initialSubjectSelected?: string[];
  onChange: (item: string[]) => void;
  isDisabled?: boolean;
};

const UNKNOWN_SUBJECT_KEY = "unknown-subject";

export function SubjectField(props: SubjectFieldProps) {
  const { testData, initialSubjectSelected = [], onChange, isDisabled } = props;
  const [searchTerm, setSearchTerm] = useState("");
  const { selected, setSelected, validatedInitialData, subjectData } = useSubjects(
    initialSubjectSelected,
    testData,
  );
  const subjectModal = useRef<HTMLIonModalElement>(null);

  const isChecked = (subject: SubjectModel) => {
    return selected.find((item) => item === subject.value) !== undefined;
  };

  const checkboxChange = (e: CheckboxCustomEvent) => {
    const { checked, value } = e.detail;
    return checked
      ? setSelected((selected) => [...selected, value])
      : setSelected(selected.filter((item) => item !== value));
  };

  const onModalDismiss = () => {
    setSelected(validatedInitialData);
    subjectModal.current?.dismiss();
  };

  const onModalConfirm = () => {
    onChange(selected);
    subjectModal.current?.dismiss();
  };

  const isItemDisabled = (item: SubjectModel) => {
    return (
      (selected.includes(translate("UNKNOWN")) && item.key !== UNKNOWN_SUBJECT_KEY) ||
      (!selected.includes(translate("UNKNOWN")) &&
        selected.length !== 0 &&
        item.key === UNKNOWN_SUBJECT_KEY)
    );
  };

  if (!subjectData) {
    return <div></div>;
  }

  const filteredSubjects = subjectData?.filter((subject: SubjectModel) =>
    subject.label.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <SelectItem
      id="subject-select-modal"
      label={translate("LABEL_SUBJECT")}
      value={translateSelectedSubjects(selected, subjectData)}
      stacked
      isDisabled={isDisabled}
      css={isDisabled ? { color: Colors.slate300, cursor: "not-allowed" } : undefined}
    >
      <IonModal
        aria-label="Subject Selection"
        trigger="subject-select-modal"
        ref={subjectModal}
        backdropDismiss={false}
        className="modal-600 stackedModal"
      >
        <ModalHeader
          title={translate("SUBJECT_MODAL_HEADER_LABEL")}
          onModalDismiss={onModalDismiss}
          onModalConfirm={onModalConfirm}
          onIonInput={(e) => setSearchTerm(e?.target?.value || "")}
        />
        <IonContent>
          <IonList lines="none">
            {filteredSubjects.map((item: SubjectModel, idx: number) => (
              <IonItem key={idx} lines="inset">
                <IonCheckbox
                  labelPlacement="end"
                  justify="start"
                  value={item.value}
                  checked={isChecked(item)}
                  onIonChange={checkboxChange}
                  disabled={isItemDisabled(item)}
                >
                  {item.label}
                </IonCheckbox>
              </IonItem>
            ))}
          </IonList>
        </IonContent>
      </IonModal>
    </SelectItem>
  );
}
